import { ChangeDetectionStrategy, Component, EventEmitter, Output } from "@angular/core";
import { Router } from "@angular/router";
import { DateHelper } from "@calaosoft/osapp-common/dates/helpers/dateHelper";
import { ObservableArray } from "@calaosoft/osapp-common/observable/models/observable-array";
import { secure } from "@calaosoft/osapp-common/rxjs/operators/secure";
import { BehaviorSubject, Observable, defer, firstValueFrom, of } from "rxjs";
import { map, mergeMap } from "rxjs/operators";
import { ActivePageManager } from "../../../../../../model/navigation/ActivePageManager";
import { PopoverService } from "../../../../../../services/popover.service";
import { CustomersService } from "../../../../../customer/services/customers.service";
import { AppointmentService } from "../../../../../tour/appointment/appointment.service";
import { EAppointmentType } from "../../../../../tour/appointment/models/EAppointmentType";
import { IAppointment } from "../../../../../tour/appointment/models/IAppointment";
import { ICustomer } from "../../../../../tour/customers/i-customer";
import { ITour } from "../../../../../tour/models/ITour";
import { TourService } from "../../../../../tour/tour/tour.service";
import { DestroyableComponentBase } from "../../../../../utils/components/destroyable-component-base";
import { IMerchandising } from "../../../../models/IMerchandising";
import { ICustomerSelectorPopoverItem } from "../customer-selector-popover-item/i-customer-selector-popover-item";
import { CustomerSelectorPopoverComponent } from "../customer-selector-popover/customer-selector-popover.component";

/** Sélectionne un client à partir de la liste des rendez-vous de la tournée. */
@Component({
	selector: "customer-selector-component",
	templateUrl: "./customer-selector.component.html",
	styleUrls: ["./customer-selector.component.scss"],
	changeDetection: ChangeDetectionStrategy.OnPush,
	standalone: false
})
export class CustomerSelectorComponent extends DestroyableComponentBase {
	//#region FIELDS

	private readonly moActivePageManager = new ActivePageManager(this, this.ioRouter);
	private static readonly C_POPOVER_CSS_CLASS = "customer-selector";

	@Output("onCustomerSelected") private readonly moOnCustomerSelected = new EventEmitter<string>();

	//#endregion FIELDS

	//#region PROPERTIES

	/** Liste des rendez-vous. La valeur est `undefined` pendant son chargement et un tableau vide s'il n'y a pas de rendez-vous. */
	public readonly appointments$: Observable<IMerchandising[] | undefined> = of(undefined);

	private readonly moSelectedItemSubject = new BehaviorSubject<undefined | ICustomerSelectorPopoverItem>(undefined);
	public readonly selectedItem$ = this.moSelectedItemSubject.asObservable().pipe(secure(this));

	private readonly moCustomers$: Observable<ICustomer[]>;

	//#endregion PROPERTIES

	//#region METHODS

	constructor(
		private readonly isvcTour: TourService,
		private readonly isvcAppointment: AppointmentService,
		private readonly ioRouter: Router,
		private isvcPopover: PopoverService,
		private readonly isvcCustomers: CustomersService
	) {
		super();
		this.appointments$ = this.getAppointments$().pipe(secure(this));
		this.moCustomers$ = this.appointments$.pipe(
			mergeMap((paAppointments: IMerchandising[]) => this.getCustomersFromAppointments(paAppointments)),
			secure(this)
		);
	}

	private getAppointments$(): Observable<IMerchandising[] | undefined> {
		return defer(() => this.isvcTour.getCurrentTourAsync()).pipe(
			mergeMap((poTour: ITour<unknown>) =>
				this.isvcAppointment.getAppointments<IAppointment>(poTour._id, this.moActivePageManager)
			),
			map(
				(paAppointments: IAppointment[]): IMerchandising[] =>
					paAppointments.filter(
						(poAppointment: IAppointment) => poAppointment.appointmentType === EAppointmentType.merchandising
					) as IMerchandising[]
			),
			map((paAppointments: IMerchandising[]) =>
				DateHelper.sortByDate<IMerchandising>(
					paAppointments,
					(poAppointment: IMerchandising) => poAppointment.expectedDate
				)
			)
		);
	}

	private getCustomersFromAppointments(paAppointments: IMerchandising[]): Observable<ICustomer[]> {
		return this.isvcCustomers.getCustomersFromIds$(
			paAppointments.map((poAppointment: IMerchandising): string => poAppointment.clientId)
		);
	}

	private onSelectionChanged(poSelectedItem: ICustomerSelectorPopoverItem): void {
		this.moOnCustomerSelected.emit(poSelectedItem.appointment.clientId);
		this.moSelectedItemSubject.next(poSelectedItem);
	}

	public async onSelectionClickedAsync(poEvent: MouseEvent): Promise<void> {
		const laAppointments: IMerchandising[] = (await firstValueFrom(this.appointments$))!;
		const laCustomers: ICustomer[] = await firstValueFrom(this.moCustomers$);

		const loPopover: CustomerSelectorPopoverComponent =
			this.isvcPopover.showCustomPopover<CustomerSelectorPopoverComponent>(
				CustomerSelectorPopoverComponent,
				{
					appointments: new ObservableArray<IMerchandising>(laAppointments),
					customers: new ObservableArray<ICustomer>(laCustomers)
				},
				poEvent,
				CustomerSelectorComponent.C_POPOVER_CSS_CLASS,
				"top"
			);

		loPopover.onChanged.subscribe((poValue: ICustomerSelectorPopoverItem) => this.onSelectionChanged(poValue));
	}

	public override ngOnDestroy(): void {
		super.ngOnDestroy();
		this.moSelectedItemSubject.complete();
	}

	//#endregion METHODS
}
